import { h, Host } from "@stencil/core";
import { theme } from "@eventstore-ui/theme";
import { Link } from "@eventstore-ui/router";
/**
 * Anchor link version of es-button, wraps a `Link` from `@eventstore-ui/router`.
 * @slot before - Placed before the main content with correct padding.
 * @slot after -  Placed after the main content with correct padding.
 * @part link - The internal a element.
 * @part inner - The inner span, wrapping the default slot.
 */
export class ButtonLink {
  constructor() {
    this.variant = 'default';
    this.disabled = undefined;
    this.url = undefined;
    this.forceRefresh = undefined;
    this.external = undefined;
    this.anchorClass = undefined;
    this.anchorRole = undefined;
    this.anchorTitle = undefined;
    this.anchorTabIndex = undefined;
    this.anchorId = undefined;
    this.target = undefined;
  }
  render() {
    return (h(Host, { "high-contrast": theme.isHighContrast(), dark: theme.isDark() }, h(Link, { url: this.url, forceRefresh: this.forceRefresh, external: this.external, class: this.anchorClass, role: this.anchorRole, title: this.anchorTitle, tabIndex: this.anchorTabIndex, id: this.anchorId, target: this.target ?? this.external ? '_blank' : undefined, rel: this.external ? 'noopener' : undefined, disabled: this.disabled, part: 'link' }, h("slot", { name: 'before' }), h("span", { part: 'inner' }, h("slot", null)), h("slot", { name: 'after' }))));
  }
  static get is() { return "es-button-link"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["../button.css"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["../button.css"]
    };
  }
  static get properties() {
    return {
      "variant": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "ButtonVariant",
          "resolved": "\"cancel\" | \"default\" | \"delete\" | \"filled\" | \"link\" | \"minimal\" | \"outline\"",
          "references": {
            "ButtonVariant": {
              "location": "import",
              "path": "../types",
              "id": "src/components/buttons/types.ts::ButtonVariant"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Which styling variant to use"
        },
        "attribute": "variant",
        "reflect": true,
        "defaultValue": "'default'"
      },
      "disabled": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If the link is disabled. Prevents the user from interacting with the link: it cannot be pressed or focused."
        },
        "attribute": "disabled",
        "reflect": true
      },
      "url": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Where the button should link to."
        },
        "attribute": "url",
        "reflect": false
      },
      "forceRefresh": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "If the button should navigate within the router context, or force a refresh."
        },
        "attribute": "force-refresh",
        "reflect": false
      },
      "external": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Link is for an external site"
        },
        "attribute": "external",
        "reflect": false
      },
      "anchorClass": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Class for the contained anchor element"
        },
        "attribute": "anchor-class",
        "reflect": false
      },
      "anchorRole": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Role for the contained anchor element"
        },
        "attribute": "anchor-role",
        "reflect": false
      },
      "anchorTitle": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Title for the contained anchor element"
        },
        "attribute": "anchor-title",
        "reflect": false
      },
      "anchorTabIndex": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Tab Index for the contained anchor element"
        },
        "attribute": "anchor-tab-index",
        "reflect": false
      },
      "anchorId": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Id for the contained anchor element"
        },
        "attribute": "anchor-id",
        "reflect": false
      },
      "target": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string | undefined",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Target for link (eg: target=\"_blank\")"
        },
        "attribute": "target",
        "reflect": false
      }
    };
  }
}
//# sourceMappingURL=es-button-link.js.map
